import { ButtonNew as Button } from "components/buttonNew";
import { CustomColorPickerPopover } from "components/colorpicker";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { WebsiteBuilderBrowseIcons } from "../../../../../helpers/WebsiteBuilderBrowseIcons";
import { IWebsiteBuilderIconAsset } from "redux/reducers/models/websiteBuilder";
import { websiteBuilderAddIconAssets, websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import { websiteBuilderFindIconAsset, WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG, WEBSITE_BUILDER_SUGGESTED_ICON_IDENTITIES, IWebsiteBuilderIconAssetIdentity, IWebsiteBuilderIconSettings, TWebsiteBuilderIconElementSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { cloneDeep } from "lodash";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { WebsiteBuilderOptionalLinkSettings } from "../../../../../helpers/WebsiteBuilderOptionalLinkSettings";

interface IIconSettingsProps {
  id: string;
  settings: TWebsiteBuilderIconElementSettings;
  closeSettings: () => void;
}

export function IconSettings(props: IIconSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<TWebsiteBuilderIconElementSettings>(() => cloneDeep(settings));

  function dispatchSettings(prevSettings: TWebsiteBuilderIconElementSettings, nextSettings: TWebsiteBuilderIconElementSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "icon",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "icon",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  return (
    <WebsiteBuilderIconSettingsView
      title={t("Icon settings")}
      settings={updatedSettings}
      onChange={nextSettings => handleUpdateState(prev => ({ ...prev, ...nextSettings }))}
      onDebouncedChange={nextSettings => handleDebouncedUpdateState(prev => ({ ...prev, ...nextSettings }))}
      closeSettings={closeSettings}
      linkSection={
        <WebsiteBuilderOptionalLinkSettings
          key="icon-link"
          link={updatedSettings.link}
          onChange={link => handleUpdateState(prev => ({ ...prev, link }))}
          onDebouncedChange={link => handleDebouncedUpdateState(prev => ({ ...prev, link }))}
        />
      }
    />
  );
}

interface IWebsiteBuilderIconSettingsViewProps {
  title: string;
  settings: IWebsiteBuilderIconSettings;
  onChange: (settings: IWebsiteBuilderIconSettings) => void;
  onDebouncedChange: (settings: IWebsiteBuilderIconSettings) => void;
  onBack?: () => void;
  closeSettings: () => void;
  linkSection?: JSX.Element;
}

interface IWebsiteBuilderIconSettingsNoSubView {
  type: "none";
}

interface IWebsiteBuilderIconSettingsBrowseAllSubView {
  type: "browse-all";
}

type TWebsiteBuilderIconSettingsSubView =
  | IWebsiteBuilderIconSettingsNoSubView
  | IWebsiteBuilderIconSettingsBrowseAllSubView;

export function WebsiteBuilderIconSettingsView(props: IWebsiteBuilderIconSettingsViewProps) {
  const { title, settings, onChange, onDebouncedChange, onBack, closeSettings, linkSection } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [subView, setSubView] = useState<TWebsiteBuilderIconSettingsSubView>({ type: "none" });

  const suggestedIconAssets = useAppSelector(store => store.websiteBuilderStore.editor.suggestedIconAssets);

  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);
  const iconAsset = websiteBuilderFindIconAsset(iconAssets, settings.assetIdentity);

  function handleSelectIcon(asset: IWebsiteBuilderIconAsset) {
    if (asset == null) {
      return;
    }

    dispatch(websiteBuilderAddIconAssets({ iconAssets: [asset] }));

    const assetIdentity: IWebsiteBuilderIconAssetIdentity = {
      providerCode: asset.provider_code,
      versionCode: asset.version_code,
      variantCode: asset.variant_code,
      assetCode: asset.asset_code,
    };

    onChange({
      ...settings,
      assetIdentity,
    });
  }

  function goBackToParentView() {
    setSubView({ type: "none" });
  }

  function renderView() {
    switch (subView.type) {
      case "browse-all":
        return (
          <WebsiteBuilderBrowseIcons
            assetIdentity={settings.assetIdentity}
            onBack={goBackToParentView}
            onSelect={asset => {
              handleSelectIcon(asset);
              goBackToParentView();
            }}
          />
        );
      case "none":
      default:
        return (
          <WebsiteBuilderPanelContent
            title={title}
            closePanel={closeSettings}
            sections={[
              <React.Fragment key="edit-icon">
                <div className="website-builder-icon-settings-preview">
                  <div className="website-builder-icon-settings-preview-container">
                    <div
                      className="website-builder-icon-settings-preview-image"
                      style={{ color: settings.color }}
                      dangerouslySetInnerHTML={{ __html: iconAsset?.svg ?? WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG }}
                    />
                  </div>
                  <div className="website-builder-icon-settings-preview-message">
                    <div className="website-builder-icon-settings-preview-primary-message">
                      {iconAsset?.svg == null ? t("No icon selected") : iconAsset.title}
                    </div>
                    {iconAsset?.svg != null && (
                      <div className="website-builder-icon-settings-preview-secondary-message">
                        {iconAsset.variant_code.charAt(0).toUpperCase() + iconAsset.variant_code.slice(1)}
                      </div>
                    )}
                  </div>
                </div>
                {suggestedIconAssets?.length > 0 && (
                  <div className="website-builder-icon-settings-options">
                    {WEBSITE_BUILDER_SUGGESTED_ICON_IDENTITIES.map(identity => {
                      const asset = websiteBuilderFindIconAsset(suggestedIconAssets, identity);

                      if (!asset?.svg) {
                        return null;
                      }

                      return (
                        <OverlayTrigger
                          key={identity.assetCode}
                          placement="left"
                          popperConfig={{ strategy: "fixed" }}
                          transition={false}
                          delay={{
                            show: 500,
                            hide: 0,
                          }}
                          overlay={
                            <Tooltip id={`website-builder-icon-settings-suggested-icon-${identity.assetCode}`}>
                              {asset.title}
                            </Tooltip>
                          }
                        >
                          <button
                            type="button"
                            aria-label={asset?.title ?? identity.assetCode}
                            aria-pressed={asset != null && asset === websiteBuilderFindIconAsset(suggestedIconAssets, settings.assetIdentity)}
                            onClick={() => handleSelectIcon(asset)}
                            dangerouslySetInnerHTML={{ __html: asset?.svg ?? "" }}
                          />
                        </OverlayTrigger>
                      );
                    })}
                  </div>
                )}
                <Button size="small" type="secondary" onClick={() => setSubView({ type: "browse-all" })} block>
                  {t("Browse all icons")}&nbsp;<FontAwesomeIcon icon={faChevronRight} />
                </Button>
                <CustomColorPickerPopover
                  title={t("Color")}
                  hexColor={settings.color}
                  updateHexColor={color => {
                    onDebouncedChange({
                      ...settings,
                      color,
                    });
                  }}
                  portalParentId={WEBSITE_BUILDER_PORTAL}
                />
              </React.Fragment>,
              ...(linkSection != null ? [linkSection] : []),
            ]}
            footer={onBack && (
              <Button size="small" type="secondary" onClick={onBack} block>
                {t("Go back")}
              </Button>
            )}
          />
        );
    }
  }

  return renderView();
}
